import { Check, X } from "lucide-react";

type Cell = boolean | string;

interface Row {
  feature: string;
  upfixe: Cell;
  agency: Cell;
  saasTools: Cell;
  readOnly: Cell;
}

const rows: Row[] = [
  { feature: "Builds campaigns from scratch", upfixe: true, agency: "Slow", saasTools: false, readOnly: false },
  { feature: "Chat-native", upfixe: true, agency: false, saasTools: false, readOnly: false },
  { feature: "Publishes to your Google Ads", upfixe: true, agency: true, saasTools: true, readOnly: false },
  { feature: "Budget-aware structure", upfixe: true, agency: true, saasTools: false, readOnly: false },
  { feature: "Multi-brand / multi-client", upfixe: true, agency: true, saasTools: "Pro tier", readOnly: false },
  { feature: "Time to first campaign", upfixe: "≈4 mins", agency: "2–4 weeks", saasTools: "n/a", readOnly: "n/a" },
  { feature: "Monthly cost", upfixe: "$99", agency: "$1,000+", saasTools: "$200+", readOnly: "Free / read-only" },
];

const renderCell = (value: Cell, highlight = false) => {
  if (value === true)
    return (
      <Check
        className={`h-5 w-5 mx-auto ${highlight ? "text-primary" : "text-foreground"}`}
        aria-label="Yes"
      />
    );
  if (value === false)
    return <X className="h-5 w-5 mx-auto text-muted-foreground/50" aria-label="No" />;
  return <span className="text-sm text-muted-foreground">{value}</span>;
};

const McpComparisonTable = () => {
  return (
    <section className="py-20 px-4 bg-muted/30 border-b border-border">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Why not an agency, or one of the dashboards?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            PPC agencies are slow and expensive. SaaS dashboards optimise what
            you've already built — they don't build it for you. Read-only MCPs
            can look but not touch. Here's where Upfixe lands.
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block rounded-lg border border-border bg-background overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                  Feature
                </th>
                <th className="p-4 text-sm font-semibold text-primary bg-primary/5">
                  Upfixe $99
                </th>
                <th className="p-4 text-sm font-semibold text-muted-foreground">
                  PPC agency
                </th>
                <th className="p-4 text-sm font-semibold text-muted-foreground">
                  Optmyzr / Adzooma / Hike
                </th>
                <th className="p-4 text-sm font-semibold text-muted-foreground">
                  Read-only MCPs
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.feature}
                  className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}
                >
                  <td className="p-4 text-sm font-medium text-foreground">
                    {row.feature}
                  </td>
                  <td className="p-4 text-center bg-primary/5">
                    {renderCell(row.upfixe, true)}
                  </td>
                  <td className="p-4 text-center">{renderCell(row.agency)}</td>
                  <td className="p-4 text-center">{renderCell(row.saasTools)}</td>
                  <td className="p-4 text-center">{renderCell(row.readOnly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-4">
          {[
            { name: "Upfixe $99", key: "upfixe" as const, highlight: true },
            { name: "PPC agency", key: "agency" as const, highlight: false },
            { name: "Optmyzr / Adzooma / Hike", key: "saasTools" as const, highlight: false },
            { name: "Read-only MCPs", key: "readOnly" as const, highlight: false },
          ].map((col) => (
            <div
              key={col.name}
              className={`rounded-lg border bg-background p-5 ${
                col.highlight ? "border-primary ring-2 ring-primary/20" : "border-border"
              }`}
            >
              <h3
                className={`font-semibold mb-3 ${
                  col.highlight ? "text-primary" : "text-foreground"
                }`}
              >
                {col.name}
              </h3>
              <ul className="space-y-2">
                {rows.map((row) => (
                  <li
                    key={row.feature}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-muted-foreground">{row.feature}</span>
                    <span>{renderCell(row[col.key], col.highlight)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default McpComparisonTable;
